import { useEffect, useRef } from 'react';
import {
  CandlestickSeries,
  createChart,
  CrosshairMode,
  LineStyle,
  type IChartApi,
  type IPriceLine,
  type ISeriesApi,
  type Time,
  type UTCTimestamp,
} from 'lightweight-charts';
import type { Candle, PriceAlert } from '@pipyar/core';
import { chartPalette } from './chartTheme';
import { showLatest } from './chartShift';
import { cn } from '@/lib/utils';
import { useLiveBar } from '@/lib/useLiveBar';

interface AlertChartProps {
  candles: Candle[];
  alerts: PriceAlert[];
  digits: number;
  theme: 'dark' | 'light';
  resetKey?: string;
  className?: string;
  /** While true, the next click on the chart places a line instead of doing nothing. */
  placing?: boolean;
  /** A line was placed, by double-click or by a click while `placing`. */
  onCreate: (price: number) => void;
  /** A line was dragged and dropped. */
  onMove: (id: number, price: number) => void;
  visibleBars?: number;
}

/** How close, in CSS pixels, the pointer must be to grab a line. */
const GRAB_PX = 6;
const GRAB_PX_TOUCH = 14;

/** Stored timestamps are broker wall-clock, so they must be read as UTC. */
function formatStoredTime(t: UTCTimestamp): string {
  const d = new Date((t as number) * 1000);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getUTCDate())}/${p(d.getUTCMonth() + 1)} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
}

/**
 * Candles with price-alert lines that can be dragged, MT5 style.
 *
 * Lightweight Charts has no draggable price lines, so the drag is done here:
 * pointer events are watched on the host in the CAPTURE phase, and a press
 * that lands on a line is stopped before the chart's canvas sees it — which is
 * what keeps the chart from panning underneath the line while it moves.
 */
export function AlertChart({
  candles,
  alerts,
  digits,
  theme,
  resetKey,
  className,
  placing = false,
  onCreate,
  onMove,
  visibleBars = 220,
}: AlertChartProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  // Last candle follows the pushed price between refetches.
  useLiveBar(seriesRef, candles);
  const linesRef = useRef(new Map<number, { line: IPriceLine; alert: PriceAlert }>());
  const positionedForRef = useRef<string | null>(null);

  /** The line being dragged, and where it currently sits. */
  const dragRef = useRef<{ id: number; price: number } | null>(null);

  // Callbacks and flags read from long-lived listeners go through refs, so the
  // listeners are bound once per chart rather than on every render.
  const onCreateRef = useRef(onCreate);
  const onMoveRef = useRef(onMove);
  const placingRef = useRef(placing);
  onCreateRef.current = onCreate;
  onMoveRef.current = onMove;
  placingRef.current = placing;

  // ---- create / destroy ----
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const palette = chartPalette(theme);
    const chart = createChart(host, {
      autoSize: true,
      layout: {
        background: { color: 'transparent' },
        textColor: palette.text,
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: palette.grid },
        horzLines: { color: palette.grid },
      },
      rightPriceScale: { borderColor: palette.border },
      timeScale: {
        borderColor: palette.border,
        timeVisible: true,
        secondsVisible: false,
        rightOffset: 8,
      },
      crosshair: {
        // Normal rather than Magnet: placing a line should land where the
        // pointer is, not snap to the nearest open or close.
        mode: CrosshairMode.Normal,
        vertLine: { color: palette.crosshair, labelBackgroundColor: palette.crosshair },
        horzLine: { color: palette.crosshair, labelBackgroundColor: palette.crosshair },
      },
      localization: {
        timeFormatter: (t: Time) => formatStoredTime(t as UTCTimestamp),
      },
    });

    const series = chart.addSeries(CandlestickSeries, {
      upColor: palette.bull,
      downColor: palette.bear,
      borderUpColor: palette.bull,
      borderDownColor: palette.bear,
      wickUpColor: palette.bull,
      wickDownColor: palette.bear,
      priceFormat: { type: 'price', precision: digits, minMove: 10 ** -digits },
    });

    chartRef.current = chart;
    seriesRef.current = series;

    const round = (p: number) => Number(p.toFixed(digits));
    const localY = (e: PointerEvent) => e.clientY - host.getBoundingClientRect().top;

    /** The line under the pointer, if any. */
    const lineAt = (y: number, touch: boolean): number | null => {
      const reach = touch ? GRAB_PX_TOUCH : GRAB_PX;
      let best: { id: number; dist: number } | null = null;
      for (const [id, { alert }] of linesRef.current) {
        const ly = series.priceToCoordinate(alert.price);
        if (ly === null) continue;
        const dist = Math.abs(ly - y);
        if (dist <= reach && (best === null || dist < best.dist)) best = { id, dist };
      }
      return best?.id ?? null;
    };

    const setHover = (on: boolean) => host.classList.toggle('pipyar-line-hover', on);

    const onDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      const id = lineAt(localY(e), e.pointerType === 'touch');
      if (id === null) return;
      // Stopped in the capture phase, so the chart never starts a pan.
      e.stopPropagation();
      e.preventDefault();
      const entry = linesRef.current.get(id)!;
      dragRef.current = { id, price: entry.alert.price };
      setHover(true);
      window.addEventListener('pointermove', onDragMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    };

    const onDragMove = (e: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      const price = series.coordinateToPrice(localY(e));
      if (price === null || !(price > 0)) return;
      drag.price = round(price);
      linesRef.current.get(drag.id)?.line.applyOptions({ price: drag.price });
    };

    const onUp = () => {
      window.removeEventListener('pointermove', onDragMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      const drag = dragRef.current;
      dragRef.current = null;
      setHover(false);
      if (!drag) return;
      const original = linesRef.current.get(drag.id)?.alert.price;
      if (original !== drag.price) onMoveRef.current(drag.id, drag.price);
    };

    // Hover feedback: the resize cursor is the only hint a line can be grabbed.
    const onHover = (e: PointerEvent) => {
      if (dragRef.current) return;
      setHover(lineAt(localY(e), e.pointerType === 'touch') !== null);
    };

    // Lightweight Charts listens for mouse AND touch events as well as pointer
    // ones, so the press has to be stopped in every form it can arrive in.
    const swallowIfOnLine = (e: MouseEvent | TouchEvent) => {
      if (dragRef.current) e.stopPropagation();
    };

    host.addEventListener('pointerdown', onDown, { capture: true });
    host.addEventListener('mousedown', swallowIfOnLine, { capture: true });
    host.addEventListener('touchstart', swallowIfOnLine, { capture: true });
    host.addEventListener('pointermove', onHover);

    const priceAt = (y: number | undefined) => {
      if (y === undefined) return null;
      const p = series.coordinateToPrice(y);
      return p === null || !(p > 0) ? null : round(p);
    };

    chart.subscribeClick((param) => {
      if (!placingRef.current) return;
      const price = priceAt(param.point?.y);
      if (price !== null) onCreateRef.current(price);
    });
    chart.subscribeDblClick((param) => {
      const price = priceAt(param.point?.y);
      if (price !== null) onCreateRef.current(price);
    });

    const lines = linesRef.current;
    return () => {
      host.removeEventListener('pointerdown', onDown, { capture: true });
      host.removeEventListener('mousedown', swallowIfOnLine, { capture: true });
      host.removeEventListener('touchstart', swallowIfOnLine, { capture: true });
      host.removeEventListener('pointermove', onHover);
      window.removeEventListener('pointermove', onDragMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      chart.remove();
      lines.clear();
      chartRef.current = null;
      seriesRef.current = null;
      positionedForRef.current = null;
    };
  }, [theme, digits]);

  // ---- candles ----
  useEffect(() => {
    seriesRef.current?.setData(
      candles.map((c) => ({
        time: c.time as UTCTimestamp,
        open: c.open,
        high: c.high,
        low: c.low,
        close: c.close,
      })),
    );
  }, [candles]);

  // ---- alert lines ----
  useEffect(() => {
    const series = seriesRef.current;
    if (!series) return;
    const palette = chartPalette(theme);
    const lines = linesRef.current;
    const seen = new Set<number>();

    for (const alert of alerts) {
      seen.add(alert.id);
      const live = alert.enabled && alert.triggeredAt === null;
      const options = {
        // A line mid-drag keeps the dragged price until it is dropped, or a
        // background refetch would yank it back under the pointer.
        price: dragRef.current?.id === alert.id ? dragRef.current.price : alert.price,
        color: live ? palette.alert : palette.alertMuted,
        lineWidth: 2 as const,
        lineStyle: live ? LineStyle.Solid : LineStyle.Dashed,
        axisLabelVisible: true,
        title: `${alert.direction === 'above' ? '▲' : '▼'} ${alert.note || 'Alert'}${
          alert.triggeredAt !== null ? ' ✓' : ''
        }`,
      };
      const existing = lines.get(alert.id);
      if (existing) {
        existing.line.applyOptions(options);
        existing.alert = alert;
      } else {
        lines.set(alert.id, { line: series.createPriceLine(options), alert });
      }
    }

    for (const [id, { line }] of lines) {
      if (!seen.has(id)) {
        series.removePriceLine(line);
        lines.delete(id);
      }
    }
  }, [alerts, theme, digits]);

  // Position once per series; re-centring on every poll would fight a scroll.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || candles.length === 0) return;
    const key = resetKey ?? '';
    if (positionedForRef.current === key) return;
    positionedForRef.current = key;
    showLatest(chart.timeScale(), candles.length - 1, visibleBars);
  }, [resetKey, candles, visibleBars]);

  return (
    <div
      ref={hostRef}
      className={cn('relative touch-none', placing && '[&_canvas]:!cursor-crosshair', className)}
    />
  );
}
